import React from 'react'
import styled, { keyframes } from 'styled-components'
import EnergySavingsLeafOutlinedIcon from '@mui/icons-material/EnergySavingsLeafOutlined'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import Tooltip from '@mui/material/Tooltip'

const GOV_UK_EPC_URL = 'https://www.gov.uk/find-energy-certificate'

const getEpc = unit => unit?.epcCertificate || unit?.epc_certificate || null
const getStatus = unit => unit?.epcSyncStatus || unit?.epc_sync_status || null

const getBandFromRating = rating => {
  const score = Number(rating)

  if (!Number.isFinite(score)) return null
  if (score >= 92) return 'A'
  if (score >= 81) return 'B'
  if (score >= 69) return 'C'
  if (score >= 55) return 'D'
  if (score >= 39) return 'E'
  if (score >= 21) return 'F'
  return 'G'
}

const normalizeBand = (band, rating) => {
  const normalized = String(band || '').trim().toUpperCase()

  if (/^[A-G]$/.test(normalized)) return normalized

  return getBandFromRating(rating)
}

const epcPalette = {
  A: { background: '#00A651', border: '#008E45', color: '#071B10' },
  B: { background: '#1AB35C', border: '#10934B', color: '#071B10' },
  C: { background: '#8CC63E', border: '#73A82F', color: '#102000' },
  D: { background: '#FFD100', border: '#D9B000', color: '#1F1B00' },
  E: { background: '#F7941D', border: '#D57A0D', color: '#241100' },
  F: { background: '#F37021', border: '#CE5B13', color: '#241100' },
  G: { background: '#ED1B3B', border: '#C8102E', color: '#FFFFFF' },
}

const getPalette = band => epcPalette[band] || {
  background: '#f8f9fd',
  border: '#e4e7f0',
  color: '#8a92a6',
}

const STATUS_META = {
  unchecked: { label: 'Not checked', tone: 'muted', title: 'This address hasn’t been checked against the register yet — press Check to look it up.' },
  pending: { label: 'Queued', tone: 'muted', title: 'Queued to check the energy certificate register.' },
  syncing: { label: 'Checking…', tone: 'info', title: 'Looking up the energy certificate register now.' },
  not_found: { label: 'No EPC found', tone: 'muted', title: 'No energy certificate is registered for this address on the government (GOV.UK) register — we checked both the domestic and non-domestic records. This is common for commercial, vacant and newer units. Select to search the GOV.UK register yourself.' },
  rate_limited: { label: 'Retrying', tone: 'warn', title: 'The register is busy — we’ll keep trying automatically.' },
  failed: { label: 'Check failed', tone: 'danger', title: 'Couldn’t reach the register. Try checking again.' },
}

const TONE = {
  muted: { background: '#f8f9fd', border: '#e4e7f0', color: '#8a92a6' },
  info: { background: '#eef4fb', border: '#d6e4f5', color: '#2c6cb0' },
  warn: { background: '#fef3c7', border: '#fcd980', color: '#b45309' },
  danger: { background: '#fdecea', border: '#f6c9c4', color: '#b00020' },
}

const UnitEpcBadge = ({ unit, compact = false, onCheck, checking = false }) => {
  const epc = getEpc(unit)
  const hasCert = epc && Object.keys(epc).length > 0

  if (hasCert) {
    const band = epc.currentEnergyEfficiencyBand || epc.current_energy_efficiency_band
    const rating = epc.currentEnergyEfficiencyRating || epc.current_energy_efficiency_rating
    const expiry = epc.expiryDate || epc.expiry_date
    const pdfUrl = epc.pdfUrl || epc.pdf_url

    const normalizedBand = normalizeBand(band, rating)
    const palette = getPalette(normalizedBand)
    const label = normalizedBand || rating || 'EPC'
    const title = expiry ? `EPC valid until ${expiry}` : 'EPC certificate available'

    const content = (
      <Badge compact={compact} title={title} palette={palette}>
        <EnergySavingsLeafOutlinedIcon />
        <span>{label}</span>
      </Badge>
    )

    if (!pdfUrl) return content

    return (
      <BadgeLink
        href={pdfUrl}
        target='_blank'
        rel='noreferrer'
        onClick={event => event.stopPropagation()}
      >
        {content}
      </BadgeLink>
    )
  }

  const rawStatus = getStatus(unit)
  const effectiveStatus = checking ? 'syncing' : (rawStatus || 'unchecked')
  const meta = STATUS_META[effectiveStatus] || STATUS_META.unchecked
  const tone = TONE[meta.tone] || TONE.muted
  const inProgress = checking || effectiveStatus === 'syncing' || effectiveStatus === 'pending'

  const handleCheck = event => {
    event.stopPropagation()
    event.preventDefault()
    if (!inProgress) onCheck?.()
  }

  const isNotFound = effectiveStatus === 'not_found'

  return (
    <StatusWrap>
      <Tooltip title={meta.title} arrow>
        {isNotFound ? (
          <StatusPill
            as='a'
            compact={compact}
            tone={tone}
            href={GOV_UK_EPC_URL}
            target='_blank'
            rel='noreferrer'
            onClick={event => event.stopPropagation()}
          >
            <span>{meta.label}</span>
            <OpenInNewRoundedIcon style={{ fontSize: compact ? 12 : 13 }} />
          </StatusPill>
        ) : (
          <StatusPill compact={compact} tone={tone}>
            {inProgress && (
              <Spinner>
                <AutorenewRoundedIcon style={{ fontSize: compact ? 13 : 15 }} />
              </Spinner>
            )}
            <span>{meta.label}</span>
          </StatusPill>
        )}
      </Tooltip>
      {onCheck && !inProgress && !isNotFound && (
        <Tooltip title='Check the energy certificate register now' arrow>
          <CheckBtn type='button' onClick={handleCheck} aria-label='Check the energy certificate register now'>
            <AutorenewRoundedIcon style={{ fontSize: compact ? 14 : 16 }} />
          </CheckBtn>
        </Tooltip>
      )}
    </StatusWrap>
  )
}

export default UnitEpcBadge

const BadgeLink = styled.a`
  text-decoration: none;
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: ${({ compact }) => compact ? '52px' : '64px'};
  height: ${({ compact }) => compact ? '24px' : '28px'};
  padding: 0 ${({ compact }) => compact ? '7px' : '9px'};
  border-radius: 6px;
  border: 1px solid ${({ palette }) => palette.border};
  background: ${({ palette }) => palette.background};
  color: ${({ palette }) => palette.color};
  font-size: ${({ compact }) => compact ? '11px' : '12px'};
  font-weight: 700;
  line-height: 1;
  box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.08);

  svg {
    font-size: ${({ compact }) => compact ? '14px' : '16px'};
  }
`

const StatusWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: ${({ compact }) => compact ? '52px' : '64px'};
  height: ${({ compact }) => compact ? '24px' : '28px'};
  padding: 0 ${({ compact }) => compact ? '8px' : '10px'};
  border-radius: 6px;
  border: 1px solid ${({ tone }) => tone.border};
  background: ${({ tone }) => tone.background};
  color: ${({ tone }) => tone.color};
  font-size: ${({ compact }) => compact ? '11px' : '12px'};
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;

  &[href] { cursor: pointer; }
  &[href]:hover { border-color: ${({ tone }) => tone.color}; background: #eef2f7; }
`

const spin = keyframes`to { transform: rotate(360deg); }`

const Spinner = styled.span`
  display: inline-flex;
  animation: ${spin} 1s linear infinite;
`

const CheckBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid #e4e7f0;
  border-radius: 6px;
  background: #fff;
  color: #5a6472;
  cursor: pointer;
  flex-shrink: 0;

  &:hover { background: #f1f5f9; color: #1f2430; border-color: #cfd6e4; }
`
